import { useRef } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  type TelInput,
  TelephoneInput,
} from '@/pages/Schedule/components/TelephoneInput';
import {
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@/ui';
import { getErrorMessage } from '@/utils/errors';
import type { OrgMember } from '../hooks/useOrganizationMembers';
import { useUpdateOrganizationMember } from '../hooks/useUpdateOrganizationMember';

type Props = {
  member: OrgMember;
  organizationId: string;
  isOpen: boolean;
  onClose: () => void;
};

type FormData = {
  name: string;
  phoneNumber: string;
  authorized: boolean;
};

// Owner-only editor for a family member's SMS contact details. Adding or
// changing the phone number sends the member a welcome text + contact card.
export const EditMemberModal = ({
  member,
  organizationId,
  isOpen,
  onClose,
}: Props) => {
  const { t } = useTranslation('settingsModal', {
    keyPrefix: 'organizations.edit_contact_modal',
  });
  const toast = useToast();
  const {
    updateMember,
    isPending,
    error,
    reset: resetMutation,
  } = useUpdateOrganizationMember(organizationId);

  const {
    register,
    reset: resetForm,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>({
    shouldFocusError: true,
    defaultValues: {
      name: member.memberName ?? '',
      phoneNumber: member.phoneNumber ?? '',
      authorized: false,
    },
  });

  const itiInstanceRef = useRef<TelInput | null>(null);

  const handleReset = () => {
    resetForm();
    resetMutation();
  };

  const onSubmit: SubmitHandler<FormData> = (data) => {
    const rawValue = itiInstanceRef.current?.getNumber() ?? '';
    let phoneNumber: string | null = null;

    // an empty phone field clears the number; anything else must be valid
    if (rawValue.trim().length > 0) {
      if (!itiInstanceRef.current?.isValidNumber()) {
        setError('phoneNumber', {});
        return;
      }
      phoneNumber = rawValue;
    }

    const phoneChanged = phoneNumber !== (member.phoneNumber ?? null);

    if (phoneNumber && phoneChanged && !data.authorized) {
      setError('authorized', { message: t('authorized.required') });
      return;
    }

    updateMember(
      member.user.id,
      { name: data.name.trim() || null, phoneNumber },
      {
        onSuccess: () => {
          toast.notify(t('success_toast'));
          onClose();
        },
      }
    );
  };

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      variant="primary"
      onClose={onClose}
      onCloseComplete={handleReset}
    >
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader>
          {t('heading', { name: member.user.name || member.user.email })}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody display="flex" flexDir="column" gap="4">
          {!!error && (
            <Alert status="error">
              <AlertIcon />
              <AlertTitle>{getErrorMessage(error)}</AlertTitle>
            </Alert>
          )}

          <FormControl>
            <FormLabel>{t('name.label')}</FormLabel>
            <Input
              placeholder={member.user.name ?? undefined}
              type="text"
              {...register('name')}
            />
          </FormControl>

          <FormControl
            isInvalid={!!errors.phoneNumber}
            sx={{ '.iti': { width: '100%' } }}
          >
            <FormLabel>{t('phone.label')}</FormLabel>
            <TelephoneInput
              itiInstanceRef={itiInstanceRef}
              {...register('phoneNumber')}
            />
            {errors.phoneNumber && (
              <FormErrorMessage>
                {errors.phoneNumber.message || t('phone.invalid')}
              </FormErrorMessage>
            )}
          </FormControl>

          <FormControl isInvalid={!!errors.authorized}>
            <FormLabel alignItems="center" display="flex" gap="2">
              <Checkbox {...register('authorized')} />
              {t('authorized.label')}
            </FormLabel>
            {errors.authorized && (
              <FormErrorMessage>{errors.authorized.message}</FormErrorMessage>
            )}
          </FormControl>
        </ModalBody>
        <ModalFooter gap="4">
          <Text color="customgray.mid" flex="1" fontSize="small">
            <Trans i18nKey="privacy" t={t}>
              When you add a text message recipient, we&apos;ll send each person
              a text welcoming them to Agenda Hero. After that, only you control
              who gets a text and when. Read our{' '}
              <Link colorScheme="dark" href="/privacy" target="_blank">
                privacy policy
              </Link>
              .
            </Trans>
          </Text>
          <Flex gap="3">
            <Button variant="secondary" onClick={onClose}>
              {t('cancel')}
            </Button>
            <Button isLoading={isPending} type="submit">
              {t('save')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
